// SAFE MODE: EXIF reader for portfolio image verification
// Reads camera metadata from image EXIF data in the browser.
// Used in the admin verification modal to confirm photographer equipment claims.

import { useState, useEffect } from 'react';

const PROFESSIONAL_MAKES = [
  'canon', 'nikon', 'sony', 'fujifilm', 'leica', 'panasonic',
  'olympus', 'pentax', 'hasselblad', 'phase one', 'sigma', 'ricoh',
];
const MOBILE_MAKES = [
  'apple', 'samsung', 'huawei', 'google', 'xiaomi', 'oppo',
  'vivo', 'oneplus', 'motorola', 'lg', 'nokia', 'realme',
];
const DRONE_MAKES = ['dji', 'autel', 'parrot', 'skydio', 'yuneec'];

function detectType(make = '') {
  const m = make.toLowerCase();
  if (DRONE_MAKES.some(d => m.includes(d)))        return 'drone';
  if (MOBILE_MAKES.some(d => m.includes(d)))       return 'mobile';
  if (PROFESSIONAL_MAKES.some(d => m.includes(d))) return 'professional';
  return 'unknown';
}

// Three fixed labels — detected one turns green, others stay gray
const LABELS = [
  { key: 'professional', label: 'كاميرا احترافية' },
  { key: 'mobile',       label: 'كاميرا جوال'     },
  { key: 'drone',        label: 'درون'             },
];

/**
 * ExifChip
 * @param {string} imageUrl - public URL of the image
 */
const ExifChip = ({ imageUrl }) => {
  const [state, setState] = useState('loading'); // 'loading' | 'done' | 'none'
  const [exif,  setExif]  = useState(null);

  useEffect(() => {
    if (!imageUrl) { setState('none'); return; }
    let cancelled = false;
    setState('loading');

    // Timeout: if EXIF reading hangs (e.g. CORS) → give up after 8 s
    const timer = setTimeout(() => {
      if (!cancelled) { cancelled = true; setState('none'); }
    }, 8000);

    const run = async () => {
      try {
        const { default: Exifr } = await import('exifr');

        // Fetch image as ArrayBuffer first (avoids CORS issues with exifr's internal fetch)
        let source = imageUrl;
        try {
          const res = await fetch(imageUrl, { mode: 'cors', cache: 'force-cache' });
          if (res.ok) source = await res.arrayBuffer();
        } catch {
          // fallback: pass URL directly — exifr will try on its own
        }

        const data = await Exifr.parse(source, {
          pick: ['Make', 'Model', 'DateTimeOriginal'],
        });

        if (cancelled) return;
        if (data?.Make) { setExif(data); setState('done'); }
        else setState('none');
      } catch {
        if (!cancelled) setState('none');
      } finally {
        clearTimeout(timer);
      }
    };

    run();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [imageUrl]);

  // ── Loading ──
  if (state === 'loading') {
    return (
      <div className="flex items-center gap-1 mt-1.5">
        <span className="w-2 h-2 rounded-full bg-slate-300 animate-pulse" />
        <span className="text-[10px] text-slate-400">جاري قراءة EXIF...</span>
      </div>
    );
  }

  // ── No EXIF ──
  if (state === 'none') {
    return (
      <div className="mt-1.5">
        <span className="text-[10px] text-slate-400 italic">لا يوجد بيانات EXIF</span>
      </div>
    );
  }

  // ── Has EXIF ──
  const detectedType = detectType(exif.Make);
  const year = exif.DateTimeOriginal
    ? new Date(exif.DateTimeOriginal).getFullYear()
    : null;

  return (
    <div className="mt-1.5 space-y-1.5">
      {/* Camera make + model — unchanged */}
      <div className="flex items-center gap-1 flex-wrap">
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
          📷 {exif.Make}{exif.Model ? ` ${exif.Model}` : ''}
        </span>
        {year && <span className="text-[10px] text-slate-400">{year}</span>}
      </div>

      {/* Three fixed type labels — detected = green, others = gray */}
      <div className="flex items-center gap-1 flex-wrap">
        {LABELS.map(({ key, label }) => {
          const active = detectedType === key;
          return (
            <span
              key={key}
              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded transition-colors ${
                active
                  ? 'bg-green-100 text-green-700 ring-1 ring-green-300'
                  : 'bg-slate-100 text-slate-400'
              }`}
            >
              {label}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default ExifChip;
